/**
 * Standardized Application Error System
 *
 * Provides consistent error handling across the entire application with:
 * - Typed error codes and categories
 * - Operational vs programming error distinction
 * - HTTP status codes for API errors
 * - User-friendly messages
 * - Structured error metadata
 *
 * @module AppError
 * @version 1.0.0
 * @date 2025-11-19
 * @related CQ-004 Error Handling Standardization
 */

// ============================================================================
// ERROR CATEGORIES & CODES
// ============================================================================

/**
 * Error categories for classification and handling
 */
export enum ErrorCategory {
  // User-facing errors (operational - expected errors)
  VALIDATION = 'VALIDATION',
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHORIZATION = 'AUTHORIZATION',
  NOT_FOUND = 'NOT_FOUND',
  CONFLICT = 'CONFLICT',
  RATE_LIMIT = 'RATE_LIMIT',

  // System errors (operational - can be recovered)
  DATABASE = 'DATABASE',
  EXTERNAL_API = 'EXTERNAL_API',
  FILE_SYSTEM = 'FILE_SYSTEM',
  NETWORK = 'NETWORK',

  // Programming errors (bugs - should not happen)
  PROGRAMMING = 'PROGRAMMING',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Specific error codes for detailed error identification
 */
export enum ErrorCode {
  // Validation errors (400)
  INVALID_INPUT = 'INVALID_INPUT',
  INVALID_FILE_TYPE = 'INVALID_FILE_TYPE',
  INVALID_FILE_SIZE = 'INVALID_FILE_SIZE',
  INVALID_UUID = 'INVALID_UUID',
  MISSING_REQUIRED_FIELD = 'MISSING_REQUIRED_FIELD',
  VALIDATION_FAILED = 'VALIDATION_FAILED',

  // Authentication errors (401)
  NOT_AUTHENTICATED = 'NOT_AUTHENTICATED',
  INVALID_TOKEN = 'INVALID_TOKEN',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  MFA_REQUIRED = 'MFA_REQUIRED',
  MFA_FAILED = 'MFA_FAILED',

  // Authorization errors (403)
  NOT_AUTHORIZED = 'NOT_AUTHORIZED',
  INSUFFICIENT_PERMISSIONS = 'INSUFFICIENT_PERMISSIONS',
  TIER_LIMIT_EXCEEDED = 'TIER_LIMIT_EXCEEDED',
  FEATURE_NOT_AVAILABLE = 'FEATURE_NOT_AVAILABLE',

  // Not found errors (404)
  RESOURCE_NOT_FOUND = 'RESOURCE_NOT_FOUND',
  PROFILE_NOT_FOUND = 'PROFILE_NOT_FOUND',
  SUBSIDY_NOT_FOUND = 'SUBSIDY_NOT_FOUND',
  USER_NOT_FOUND = 'USER_NOT_FOUND',
  ORGANIZATION_NOT_FOUND = 'ORGANIZATION_NOT_FOUND',

  // Conflict errors (409)
  RESOURCE_ALREADY_EXISTS = 'RESOURCE_ALREADY_EXISTS',
  DUPLICATE_ENTRY = 'DUPLICATE_ENTRY',
  CONCURRENT_MODIFICATION = 'CONCURRENT_MODIFICATION',

  // Rate limiting (429)
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',

  // Database errors (500)
  DATABASE_CONNECTION_FAILED = 'DATABASE_CONNECTION_FAILED',
  DATABASE_QUERY_FAILED = 'DATABASE_QUERY_FAILED',
  DATABASE_TIMEOUT = 'DATABASE_TIMEOUT',
  DATABASE_CONSTRAINT_VIOLATION = 'DATABASE_CONSTRAINT_VIOLATION',

  // External API errors (502/503)
  EXTERNAL_API_ERROR = 'EXTERNAL_API_ERROR',
  EXTERNAL_API_TIMEOUT = 'EXTERNAL_API_TIMEOUT',
  EXTERNAL_API_UNAVAILABLE = 'EXTERNAL_API_UNAVAILABLE',
  ANTHROPIC_API_ERROR = 'ANTHROPIC_API_ERROR',
  SUPABASE_ERROR = 'SUPABASE_ERROR',
  INSEE_API_ERROR = 'INSEE_API_ERROR',

  // File system errors (500)
  FILE_READ_ERROR = 'FILE_READ_ERROR',
  FILE_WRITE_ERROR = 'FILE_WRITE_ERROR',
  FILE_DELETE_ERROR = 'FILE_DELETE_ERROR',
  FILE_UPLOAD_ERROR = 'FILE_UPLOAD_ERROR',

  // Network errors (500/503)
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  CONNECTION_ERROR = 'CONNECTION_ERROR',

  // Programming errors (500)
  NULL_REFERENCE = 'NULL_REFERENCE',
  TYPE_ERROR = 'TYPE_ERROR',
  ASSERTION_FAILED = 'ASSERTION_FAILED',
  NOT_IMPLEMENTED = 'NOT_IMPLEMENTED',
  INVALID_STATE = 'INVALID_STATE',

  // Unknown errors (500)
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
}

/**
 * HTTP status codes for API responses
 */
export enum HttpStatus {
  OK = 200,
  CREATED = 201,
  NO_CONTENT = 204,
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  CONFLICT = 409,
  UNPROCESSABLE_ENTITY = 422,
  TOO_MANY_REQUESTS = 429,
  INTERNAL_SERVER_ERROR = 500,
  BAD_GATEWAY = 502,
  SERVICE_UNAVAILABLE = 503,
  GATEWAY_TIMEOUT = 504,
}

// ============================================================================
// ERROR METADATA INTERFACES
// ============================================================================

/**
 * Additional context about the error
 */
export interface ErrorMetadata {
  /** Resource ID that caused the error */
  resourceId?: string;

  /** Resource type (profile, subsidy, user, etc.) */
  resourceType?: string;

  /** User ID if available */
  userId?: string;

  /** Request ID for tracking */
  requestId?: string;

  /** Component/service where error occurred */
  component?: string;

  /** Action being performed when error occurred */
  action?: string;

  /** Validation errors if applicable */
  validationErrors?: Record<string, string[]>;

  /** Original error if wrapping */
  originalError?: Error;

  /** Additional context data */
  [key: string]: any;
}

/**
 * Error recovery suggestions
 */
export interface ErrorRecovery {
  /** Can the operation be retried? */
  canRetry: boolean;

  /** Suggested retry delay in ms */
  retryAfter?: number;

  /** Fallback action description */
  fallbackAction?: string;

  /** User-facing recovery instructions */
  userInstructions?: string;
}

// ============================================================================
// APP ERROR CLASS
// ============================================================================

/**
 * Custom application error class with rich context
 *
 * @example
 * ```typescript
 * throw new AppError({
 *   code: ErrorCode.PROFILE_NOT_FOUND,
 *   message: 'Profile not found',
 *   category: ErrorCategory.NOT_FOUND,
 *   statusCode: HttpStatus.NOT_FOUND,
 *   isOperational: true,
 *   metadata: { profileId: '123' }
 * });
 * ```
 */
export class AppError extends Error {
  /** Error code for programmatic handling */
  public readonly code: ErrorCode;

  /** Error category for classification */
  public readonly category: ErrorCategory;

  /** HTTP status code for API responses */
  public readonly statusCode: HttpStatus;

  /** Is this an operational error (expected) or programming error (bug)? */
  public readonly isOperational: boolean;

  /** User-friendly message (safe to show to end users) */
  public readonly userMessage: string;

  /** Additional error context and metadata */
  public readonly metadata: ErrorMetadata;

  /** Error recovery information */
  public readonly recovery: ErrorRecovery;

  /** Timestamp when error occurred */
  public readonly timestamp: Date;

  /** Stack trace (inherited from Error) */
  public readonly stack?: string;

  constructor(config: {
    code: ErrorCode;
    message: string;
    category?: ErrorCategory;
    statusCode?: HttpStatus;
    isOperational?: boolean;
    userMessage?: string;
    metadata?: ErrorMetadata;
    recovery?: Partial<ErrorRecovery>;
    cause?: Error;
  }) {
    super(config.message);

    // Set error name to class name for better stack traces
    this.name = this.constructor.name;

    // Core error properties
    this.code = config.code;
    this.category = config.category ?? this.inferCategory(config.code);
    this.statusCode = config.statusCode ?? this.inferStatusCode(this.category);
    this.isOperational = config.isOperational ?? this.isOperationalError(this.category);

    // User-facing message (sanitized)
    this.userMessage = config.userMessage ?? this.generateUserMessage(config.code, config.message);

    // Metadata and context
    this.metadata = {
      ...config.metadata,
      originalError: config.cause,
    };

    // Recovery information
    this.recovery = {
      canRetry: config.recovery?.canRetry ?? this.canRetryByCategory(this.category),
      retryAfter: config.recovery?.retryAfter,
      fallbackAction: config.recovery?.fallbackAction,
      userInstructions: config.recovery?.userInstructions ?? this.generateUserInstructions(this.category),
    };

    // Timestamp
    this.timestamp = new Date();

    // Capture stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }

    // Preserve original error stack if available
    if (config.cause?.stack) {
      this.stack = `${this.stack}\nCaused by: ${config.cause.stack}`;
    }
  }

  /**
   * Infer error category from error code
   */
  private inferCategory(code: ErrorCode): ErrorCategory {
    if (code.includes('VALIDATION') || code.includes('INVALID')) {
      return ErrorCategory.VALIDATION;
    }
    if (code.includes('AUTH') || code.includes('TOKEN') || code.includes('SESSION')) {
      return ErrorCategory.AUTHENTICATION;
    }
    if (code.includes('PERMISSION') || code.includes('AUTHORIZED') || code.includes('TIER')) {
      return ErrorCategory.AUTHORIZATION;
    }
    if (code.includes('NOT_FOUND')) {
      return ErrorCategory.NOT_FOUND;
    }
    if (code.includes('DUPLICATE') || code.includes('CONFLICT')) {
      return ErrorCategory.CONFLICT;
    }
    if (code.includes('RATE_LIMIT')) {
      return ErrorCategory.RATE_LIMIT;
    }
    if (code.includes('DATABASE')) {
      return ErrorCategory.DATABASE;
    }
    if (code.includes('API') || code.includes('ANTHROPIC') || code.includes('SUPABASE')) {
      return ErrorCategory.EXTERNAL_API;
    }
    if (code.includes('FILE')) {
      return ErrorCategory.FILE_SYSTEM;
    }
    if (code.includes('NETWORK') || code.includes('TIMEOUT') || code.includes('CONNECTION')) {
      return ErrorCategory.NETWORK;
    }
    if (code.includes('NULL') || code.includes('TYPE') || code.includes('ASSERTION')) {
      return ErrorCategory.PROGRAMMING;
    }
    return ErrorCategory.UNKNOWN;
  }

  /**
   * Infer HTTP status code from error category
   */
  private inferStatusCode(category: ErrorCategory): HttpStatus {
    switch (category) {
      case ErrorCategory.VALIDATION:
        return HttpStatus.BAD_REQUEST;
      case ErrorCategory.AUTHENTICATION:
        return HttpStatus.UNAUTHORIZED;
      case ErrorCategory.AUTHORIZATION:
        return HttpStatus.FORBIDDEN;
      case ErrorCategory.NOT_FOUND:
        return HttpStatus.NOT_FOUND;
      case ErrorCategory.CONFLICT:
        return HttpStatus.CONFLICT;
      case ErrorCategory.RATE_LIMIT:
        return HttpStatus.TOO_MANY_REQUESTS;
      case ErrorCategory.EXTERNAL_API:
        return HttpStatus.BAD_GATEWAY;
      default:
        return HttpStatus.INTERNAL_SERVER_ERROR;
    }
  }

  /**
   * Determine if error is operational (expected) based on category
   */
  private isOperationalError(category: ErrorCategory): boolean {
    return category !== ErrorCategory.PROGRAMMING && category !== ErrorCategory.UNKNOWN;
  }

  /**
   * Determine if error can be retried based on category
   */
  private canRetryByCategory(category: ErrorCategory): boolean {
    return [
      ErrorCategory.NETWORK,
      ErrorCategory.EXTERNAL_API,
      ErrorCategory.DATABASE,
      ErrorCategory.RATE_LIMIT,
    ].includes(category);
  }

  /**
   * Generate user-friendly error message
   */
  private generateUserMessage(code: ErrorCode, technicalMessage: string): string {
    // Map common error codes to user-friendly messages
    const userMessages: Partial<Record<ErrorCode, string>> = {
      [ErrorCode.NOT_AUTHENTICATED]: 'Please sign in to continue',
      [ErrorCode.NOT_AUTHORIZED]: 'You do not have permission to perform this action',
      [ErrorCode.PROFILE_NOT_FOUND]: 'Profile not found',
      [ErrorCode.SUBSIDY_NOT_FOUND]: 'Subsidy not found',
      [ErrorCode.RATE_LIMIT_EXCEEDED]: 'Too many requests. Please try again later',
      [ErrorCode.DATABASE_CONNECTION_FAILED]: 'Unable to connect to database. Please try again',
      [ErrorCode.EXTERNAL_API_TIMEOUT]: 'The request is taking longer than expected. Please try again',
      [ErrorCode.FILE_UPLOAD_ERROR]: 'Failed to upload file. Please try again',
      [ErrorCode.NETWORK_ERROR]: 'Network error. Please check your connection',
      [ErrorCode.UNKNOWN_ERROR]: 'An unexpected error occurred. Please try again',
    };

    return userMessages[code] ?? 'An error occurred. Please try again or contact support if the problem persists.';
  }

  /**
   * Generate user instructions for error recovery
   */
  private generateUserInstructions(category: ErrorCategory): string {
    const instructions: Partial<Record<ErrorCategory, string>> = {
      [ErrorCategory.AUTHENTICATION]: 'Please sign in again',
      [ErrorCategory.AUTHORIZATION]: 'Contact your administrator for access',
      [ErrorCategory.NOT_FOUND]: 'The requested resource may have been deleted or moved',
      [ErrorCategory.RATE_LIMIT]: 'Please wait a moment before trying again',
      [ErrorCategory.NETWORK]: 'Check your internet connection and try again',
      [ErrorCategory.EXTERNAL_API]: 'The service is temporarily unavailable. Please try again later',
    };

    return instructions[category] ?? 'Please try again or contact support if the problem persists';
  }

  /**
   * Convert error to JSON for logging/API responses
   */
  toJSON() {
    return {
      name: this.name,
      code: this.code,
      category: this.category,
      message: this.message,
      userMessage: this.userMessage,
      statusCode: this.statusCode,
      isOperational: this.isOperational,
      timestamp: this.timestamp.toISOString(),
      metadata: this.metadata,
      recovery: this.recovery,
      stack: this.stack,
    };
  }

  /**
   * Get sanitized error for API response (excludes sensitive data)
   */
  toAPIResponse() {
    return {
      error: {
        code: this.code,
        message: this.userMessage,
        statusCode: this.statusCode,
        timestamp: this.timestamp.toISOString(),
        canRetry: this.recovery.canRetry,
        retryAfter: this.recovery.retryAfter,
        userInstructions: this.recovery.userInstructions,
        // Only include safe metadata
        ...(this.metadata.requestId && { requestId: this.metadata.requestId }),
      },
    };
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if error is an AppError instance
 */
export function isAppError(error: any): error is AppError {
  return error instanceof AppError;
}

/**
 * Check if error is operational (safe to handle/retry)
 */
export function isOperationalError(error: any): boolean {
  if (isAppError(error)) {
    return error.isOperational;
  }
  return false;
}

/**
 * Extract error message safely from any error type
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'An unknown error occurred';
}

/**
 * Extract error code safely from any error type
 */
export function getErrorCode(error: unknown): ErrorCode {
  if (isAppError(error)) {
    return error.code;
  }
  return ErrorCode.UNKNOWN_ERROR;
}
